import type { ColumnVideo } from "../screens/columns";

type WindowsWebViewBridge = {
  postMessage: (message: unknown) => void;
  addEventListener?: (
    type: "message",
    listener: (event: { data?: unknown }) => void,
  ) => void;
  removeEventListener?: (
    type: "message",
    listener: (event: { data?: unknown }) => void,
  ) => void;
};

type MediaVariant = NonNullable<ColumnVideo["mediaVariants"]>[number];

export function isWindowsCompatibleMediaVariant(variant: MediaVariant): boolean {
  const description = [
    variant.codec,
    variant.label,
    variant.id,
    variant.format,
    variant.url,
  ]
    .filter(Boolean)
    .join(" ");
  return !/(?:vvc|vcc|h\.?\s*266|(?:^|[^a-z0-9])a[vc]2(?:[^a-z0-9]|$))/i.test(
    description,
  );
}

function nativeBridge(): WindowsWebViewBridge | null {
  if (typeof window === "undefined") return null;
  const hostWindow = window as unknown as {
    chrome?: { webview?: WindowsWebViewBridge };
    __NEPTUNE_WINDOWS_PLAYER__?: {
      version?: string;
      nativePlayback?: boolean;
    };
  };
  if (hostWindow.__NEPTUNE_WINDOWS_PLAYER__?.nativePlayback !== true) {
    return null;
  }
  return hostWindow.chrome?.webview || null;
}

export function isWindowsNativePlaybackActive(): boolean {
  return nativeBridge() !== null;
}

function videoPayload(video: ColumnVideo, preferredSourceId = "") {
  return {
    id: video.id,
    title: video.title,
    thumbnail: video.thumbnail,
    videoUrl: video.videoUrl,
    desktopVideoUrl: video.desktopVideoUrl,
    mediaVariants: (video.mediaVariants || []).filter(
      isWindowsCompatibleMediaVariant,
    ),
    preferredSourceId,
  };
}

/**
 * Passes the complete media-version list to the Windows host. Normal browsers
 * do not expose the WebView bridge, so this remains a no-op on the website.
 */
export function notifyWindowsPlayerVideoOpen(video: ColumnVideo): boolean {
  const bridge = nativeBridge();
  if (!bridge) return false;
  bridge.postMessage({
    type: "neptune-video-open",
    video: videoPayload(video),
  });
  return true;
}

export function notifyWindowsPlayerSourceSelection(
  video: ColumnVideo,
  sourceId: string,
): boolean {
  const bridge = nativeBridge();
  if (!bridge) return false;
  bridge.postMessage({
    type: "neptune-video-open",
    video: videoPayload(video, sourceId),
  });
  return true;
}

export function notifyWindowsPlayerVideoClose(): boolean {
  const bridge = nativeBridge();
  if (!bridge) return false;
  bridge.postMessage({ type: "neptune-video-close" });
  return true;
}

export function notifyWindowsPlayerSlot(
  videoId: string,
  rect: { x: number; y: number; width: number; height: number; visible: boolean },
): boolean {
  const bridge = nativeBridge();
  if (!bridge) return false;
  bridge.postMessage({
    type: "neptune-player-slot",
    slot: { videoId, ...rect },
  });
  return true;
}

export type WindowsPlayerCommand =
  | "toggle-pause"
  | "seek-backward"
  | "seek-forward"
  | "seek-to"
  | "mute"
  | "set-volume";

export function notifyWindowsPlayerCommand(
  command: WindowsPlayerCommand,
  value?: number,
): boolean {
  const bridge = nativeBridge();
  if (!bridge) return false;
  bridge.postMessage({ type: "neptune-player-command", command, value });
  return true;
}

export function subscribeWindowsPlayerProgress(
  videoId: string,
  onProgress: (positionSeconds: number, durationSeconds: number) => void,
): () => void {
  const bridge = nativeBridge();
  if (!bridge?.addEventListener || !bridge.removeEventListener) return () => {};
  const listener = (event: { data?: unknown }) => {
    let payload = event.data;
    if (typeof payload === "string") {
      try {
        payload = JSON.parse(payload);
      } catch {
        return;
      }
    }
    if (!payload || typeof payload !== "object") return;
    const message = payload as {
      type?: string;
      videoId?: string;
      positionSeconds?: number;
      durationSeconds?: number;
    };
    if (
      message.type !== "neptune-player-progress" ||
      message.videoId !== videoId
    ) {
      return;
    }
    onProgress(
      Math.max(0, Number(message.positionSeconds) || 0),
      Math.max(0, Number(message.durationSeconds) || 0),
    );
  };
  bridge.addEventListener("message", listener);
  return () => bridge.removeEventListener?.("message", listener);
}
